/**
 * Run History — immutable log of every Planning Engine run for the active
 * scenario (doc 102 §3: "every planning decision produces evidence...
 * every version is immutable and auditable").
 *
 * planning_runs is a single-row-per-instance table, always overwritten on
 * the next "Sync & Run" — there was previously no way to see what a
 * scenario's output looked like before its last run. This view reads
 * planning_run_history instead, which upsertPlanningRun logs to on every
 * run without ever overwriting a prior entry. Nothing here is editable or
 * deletable.
 *
 * Two ways back (owner ruling #10, 2026-09-22 — "go back to an earlier run's
 * results"):
 *   - "Make this run active" brings the run's saved results back as the
 *     active result for its scope (restore_planning_run, migration 769 — one
 *     governed transaction with a written reason and an audit event). A run
 *     changes nothing until "Propose", so this is the undo.
 *   - "Use this run's scope" re-selects the scope to run again on today's data.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, History, RotateCcw } from 'lucide-react';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import {
  getPlanningRunHistory,
  listPlanningRuns,
  engineInputKindLabel,
  type PlanningRunHistoryEntry,
} from '../../../lib/planningEngineService';
import { ReverseActionButton } from '../../../components/reversals/ReverseActionButton';
import { restorePlanningRun } from '../../../lib/reversals/reversalsService';
import { MAKE_RUN_ACTIVE_LABEL } from '../../../lib/planning/vocabulary';
import type { PriorityFeasibility } from '../../../lib/planningEngineModule.types';
import { activeRunHistoryIds, inputSourceFromRun, isCurrentScope } from './runHistoryScope';
import { FEASIBILITY_PLAIN_LANGUAGE, feasibilityTone, type FeasibilityTone } from '../../../lib/planningFeasibilityLanguage';

const FEASIBILITY_BADGE_CLASS: Record<FeasibilityTone, string> = {
  green: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]',
  amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]',
  red: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]',
  neutral: 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20',
};

function feasibilityBadge(f: PriorityFeasibility) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${FEASIBILITY_BADGE_CLASS[feasibilityTone(f)]}`}>
      {f}
    </span>
  );
}

function formatRunTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function scopeLabel(entry: PlanningRunHistoryEntry): string {
  if (entry.inputSourceType === 'live') return 'All Live Items';
  return entry.inputSourceName?.trim() || engineInputKindLabel(entry.inputSourceType);
}

/**
 * Put the engine back on this run's scope so it can be re-run against today's
 * data — a new run with its own row. Bringing back the run's own saved results
 * is MakeRunActiveAction (769).
 */
function RestoreScopeAction({ entry }: { entry: PlanningRunHistoryEntry }) {
  const { engineInput, setEngineInput } = usePlanningEngine();
  const source = inputSourceFromRun(entry);

  if (!source) {
    return (
      <p className="text-xs text-[var(--text-ter)]">
        This run’s scope named a record that is no longer addressable, so it cannot be restored.
        Re-running as “All Live Items” would widen the scope rather than reproduce it.
      </p>
    );
  }
  if (isCurrentScope(engineInput, entry)) {
    return (
      <p className="text-xs text-[var(--text-ter)]">
        The engine is already scoped this way — run it again from the Overview tab.
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEngineInput(source)}
      className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-3 py-1.5 text-sm text-[var(--text-pri)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50"
    >
      <RotateCcw className="h-3.5 w-3.5" />
      Use this run’s scope
    </button>
  );
}

/**
 * Make an earlier run's saved results the active result for its scope again
 * (restore_planning_run, 769). After it lands, the engine is pointed at that
 * run's scope so every tab shows the restored numbers.
 */
function MakeRunActiveAction({
  entry,
  isActive,
  onRestored,
}: {
  entry: PlanningRunHistoryEntry;
  isActive: boolean;
  onRestored: () => void;
}) {
  const { engineInput, setEngineInput, reloadActiveRun } = usePlanningEngine();

  if (isActive) {
    return (
      <p className="text-xs text-[var(--text-ter)]">
        These are the active results for this run’s scope.
      </p>
    );
  }
  if (!entry.hasSavedResults) {
    return (
      <p className="text-xs text-[var(--text-ter)]">
        This run was logged before full results were kept, so its results cannot be brought back.
        Use its scope and run the engine again.
      </p>
    );
  }
  const source = inputSourceFromRun(entry);
  return (
    <ReverseActionButton
      label={MAKE_RUN_ACTIVE_LABEL}
      confirmTitle="Go back to this run’s results"
      explain={
        <>
          The results of the run on {formatRunTime(entry.runCompletedAt ?? entry.runStartedAt)} become the
          active results for {scopeLabel(entry)} again. Nothing is committed and no plan version changes —
          the run you replace stays in this history and can be made active again.
        </>
      }
      run={(reason) => restorePlanningRun(entry.id, reason)}
      onDone={() => {
        if (source && !isCurrentScope(engineInput, entry)) {
          setEngineInput(source);
        } else {
          void reloadActiveRun();
        }
        onRestored();
      }}
    />
  );
}

function RunHistoryDetailPanel({
  entry,
  isActive,
  onRestored,
}: {
  entry: PlanningRunHistoryEntry;
  isActive: boolean;
  onRestored: () => void;
}) {
  const s = entry.executiveSummary;
  return (
    <div className="p-6 space-y-5 h-full overflow-auto">
      <div>
        <h3 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {formatRunTime(entry.runCompletedAt ?? entry.runStartedAt)}
        </h3>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
          {scopeLabel(entry)}
          {entry.scopedItemCount != null ? ` · ${entry.scopedItemCount} item${entry.scopedItemCount !== 1 ? 's' : ''} in scope` : ''}
        </p>
        {isActive && <ActiveBadge />}
      </div>
      <div>
        <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Overall feasibility</p>
        {feasibilityBadge(s.overallFeasibility)}
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
          {FEASIBILITY_PLAIN_LANGUAGE[s.overallFeasibility]}
        </p>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div><dt className="text-[var(--text-ter)]">Total rows</dt><dd className="tabular-nums font-medium">{s.totalWorkUnits}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Included</dt><dd className="tabular-nums">{s.includedCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Out - capacity</dt><dd className="tabular-nums">{s.deferredCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Blocked</dt><dd className="tabular-nums">{s.blockedCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Not Ready</dt><dd className="tabular-nums">{s.notReadyCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Needs Sizing</dt><dd className="tabular-nums">{s.needsSizingCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">No Capacity Model</dt><dd className="tabular-nums">{s.noCapacityModelCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Participation at Risk</dt><dd className="tabular-nums">{s.participationAtRiskCount}</dd></div>
        <div><dt className="text-[var(--text-ter)]">Participation Blocked</dt><dd className="tabular-nums">{s.participationBlockedCount}</dd></div>
      </dl>
      <div className="border-t border-[var(--rule)] pt-4 space-y-2">
        <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">
          Go back to this run
        </p>
        <MakeRunActiveAction entry={entry} isActive={isActive} onRestored={onRestored} />
      </div>
      <div className="border-t border-[var(--rule)] pt-4">
        <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-2">
          Run it again
        </p>
        <RestoreScopeAction entry={entry} />
      </div>
    </div>
  );
}

function ActiveBadge() {
  return (
    <span className="mt-2 inline-flex items-center gap-1 rounded bg-[var(--green-dim)] px-2 py-0.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] dark:bg-[var(--green)]/20">
      <CheckCircle2 className="h-3 w-3" />
      Active results
    </span>
  );
}

const SORT_OPTIONS: SortOption<'runCompletedAt'>[] = [
  { field: 'runCompletedAt', label: 'Run time', defaultDirection: 'desc' },
];

const FACETS: FacetDefinition<string>[] = [
  {
    key: 'executiveSummary.overallFeasibility',
    label: 'Feasibility',
    options: [
      { value: 'achievable', label: 'Achievable' },
      { value: 'at_risk', label: 'At Risk' },
      { value: 'not_achievable', label: 'Not Achievable' },
    ],
  },
];

export default function EngineRunHistoryView() {
  const { projectId, activeScenario } = usePlanningEngine();
  const [entries, setEntries] = useState<PlanningRunHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeIds, setActiveIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!projectId || !activeScenario) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [rows, instances] = await Promise.all([
        getPlanningRunHistory(projectId, activeScenario.id),
        listPlanningRuns(projectId, activeScenario.id),
      ]);
      setEntries(rows);
      setActiveIds(activeRunHistoryIds(rows, instances));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, activeScenario]);

  useEffect(() => {
    void load();
  }, [load]);

  const tableColumns: TableColumnWithRender<PlanningRunHistoryEntry>[] = useMemo(
    () => [
      {
        key: 'runCompletedAt',
        label: 'Run time',
        sortable: true,
        minWidth: 170,
        render: (e) => (
          <span className="inline-flex items-center gap-1.5">
            {formatRunTime(e.runCompletedAt ?? e.runStartedAt)}
            {activeIds.has(e.id) && (
              <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" aria-label="Active results" />
            )}
          </span>
        ),
      },
      { key: 'scope', label: 'Input Scope', minWidth: 160, flex: true, render: (e) => scopeLabel(e) },
      {
        key: 'feasibility',
        label: 'Feasibility',
        minWidth: 110,
        render: (e) => feasibilityBadge(e.executiveSummary.overallFeasibility),
      },
      { key: 'included', label: 'Included', align: 'right', minWidth: 72, render: (e) => <span className="tabular-nums">{e.executiveSummary.includedCount}</span> },
      { key: 'deferred', label: 'Out - capacity', align: 'right', minWidth: 112, render: (e) => <span className="tabular-nums">{e.executiveSummary.deferredCount}</span> },
      { key: 'blocked', label: 'Blocked', align: 'right', minWidth: 72, render: (e) => <span className="tabular-nums">{e.executiveSummary.blockedCount}</span> },
      { key: 'notReady', label: 'Not Ready', align: 'right', minWidth: 80, render: (e) => <span className="tabular-nums">{e.executiveSummary.notReadyCount}</span> },
    ],
    [activeIds],
  );

  return (
    <PlanningEntityListPage
      items={entries}
      loading={loading}
      getId={(e) => e.id}
      title="Run History"
      subtitle={
        activeScenario
          ? `${entries.length} run${entries.length !== 1 ? 's' : ''} logged for ${activeScenario.name} — every "Sync & Run", never overwritten. Open a run to make its results active again.`
          : 'Select a scenario to see its run history'
      }
      icon={<History className="h-4 w-4 text-[var(--text-ter)]" />}
      itemLabel="run"
      sortOptions={SORT_OPTIONS}
      defaultSort={{ field: 'runCompletedAt', direction: 'desc' }}
      facets={FACETS}
      tableColumns={tableColumns}
      renderDetail={(e) => (
        <RunHistoryDetailPanel entry={e} isActive={activeIds.has(e.id)} onRestored={() => void load()} />
      )}
      selectedId={selectedId}
      onSelectionChange={setSelectedId}
      urlParamName="run"
      onRefresh={() => void load()}
      emptyTitle="No runs logged yet"
      emptyDescription="Click Sync & Run on the Overview tab — every run this scenario makes gets logged here, never overwritten."
      error={error}
      activeScenario={activeScenario}
    />
  );
}
